import axios from 'axios';
import { User } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'veterinaire' | 'proprietaire';
  telephone?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
}

export interface UpdateUserData {
  email?: string;
  first_name?: string;
  last_name?: string;
  telephone?: string;
  adresse?: string;
  code_postal?: string;
  ville?: string;
  pays?: string;
  is_active?: boolean;
  password?: string;
  latitude?: number;
  longitude?: number;
  veterinaire_selectionne?: number | null;
}

export interface UserListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: User[];
}

export interface UserStats {
  totalUsers: number;
  adminUsers: number;
  veterinaires: number;
  proprietaires: number;
  activeUsers: number;
  inactiveUsers: number;
}

// Configuration axios avec intercepteurs pour JWT
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Intercepteur pour ajouter le token JWT
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Intercepteur pour gérer les erreurs d'authentification
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token expiré, tenter de le rafraîchir
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          const newToken = response.data.access;
          localStorage.setItem('access_token', newToken);
          
          // Retry la requête originale avec le nouveau token
          error.config.headers.Authorization = `Bearer ${newToken}`;
          return api.request(error.config);
        } catch (refreshError) {
          // Échec du refresh, rediriger vers login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const userService = {
  // Récupérer tous les utilisateurs (admin seulement)
  async getAll(params?: {
    page?: number;
    page_size?: number;
    role?: string;
    is_active?: boolean;
    search?: string;
  }): Promise<UserListResponse> {
    const response = await api.get('/users/', { params });
    return response.data;
  },

  // Récupérer un utilisateur par ID
  async getById(id: number): Promise<User> {
    const response = await api.get(`/users/${id}/`);
    return response.data;
  },

  // Récupérer le profil de l'utilisateur connecté
  async getCurrentUser(): Promise<User> {
    const response = await api.get('/users/me/');
    return response.data;
  },

  // Récupérer tous les vétérinaires
  async getVeterinaires(): Promise<UserListResponse> {
    const response = await api.get('/users/', { 
      params: { role: 'veterinaire', is_active: true } 
    });
    return response.data;
  },

  // Créer un nouvel utilisateur (admin seulement)
  async create(userData: CreateUserData): Promise<User> {
    const response = await api.post('/users/', userData);
    return response.data;
  },

  // Mettre à jour un utilisateur
  async update(id: number, userData: UpdateUserData): Promise<User> {
    const response = await api.patch(`/users/${id}/`, userData);
    return response.data;
  },

  // Mettre à jour partiellement un utilisateur
  async partialUpdate(id: number, userData: Partial<UpdateUserData>): Promise<User> {
    const response = await api.patch(`/users/${id}/`, userData);
    return response.data;
  },

  // Désactiver un utilisateur (soft delete)
  async deactivate(id: number): Promise<User> {
    const response = await api.patch(`/users/${id}/`, { is_active: false });
    return response.data;
  },

  // Réactiver un utilisateur
  async activate(id: number): Promise<User> {
    const response = await api.patch(`/users/${id}/`, { is_active: true });
    return response.data;
  },

  // Supprimer définitivement un utilisateur (admin seulement)
  async delete(id: number): Promise<void> {
    await api.delete(`/users/${id}/`);
  },

  // Récupérer les statistiques des utilisateurs
  async getStats(): Promise<UserStats> {
    const response = await api.get('/users/stats/');
    return response.data;
  },

  // Changer le mot de passe d'un utilisateur
  async changePassword(id: number, oldPassword: string, newPassword: string): Promise<void> {
    await api.post(`/users/${id}/change_password/`, {
      old_password: oldPassword,
      new_password: newPassword,
    });
  },

  // Réinitialiser le mot de passe d'un utilisateur (admin seulement)
  async resetPassword(id: number, newPassword: string): Promise<void> {
    await api.post(`/users/${id}/reset_password/`, {
      new_password: newPassword,
    });
  },

  // Rechercher des utilisateurs
  async search(query: string, filters?: {
    role?: string;
    is_active?: boolean;
  }): Promise<UserListResponse> {
    const params = {
      search: query,
      ...filters,
    };
    const response = await api.get('/users/', { params });
    return response.data;
  },

  // Exporter la liste des utilisateurs (CSV)
  async exportUsers(format: 'csv' | 'xlsx' = 'csv'): Promise<Blob> {
    const response = await api.get(`/users/export/`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  // Importer des utilisateurs depuis un fichier
  async importUsers(file: File): Promise<{ success: number; errors: string[] }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await api.post('/users/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
}; 